'use strict';

/**
 * Custom element wrapping the single header search field of the theme.
 *
 * On desktop the field is always visible. On mobile it is collapsed and gets
 * revealed under the header row when the magnifier button is pressed, then
 * focused, so the visitor never leaves the current page to search.
 *
 * There is deliberately only ONE search input in the header markup (shared by
 * both breakpoints) so third party search engines can bind their layer to a
 * single stable selector: `#header-search-input`.
 *
 * When such a layer is bound (Doofinder), the magnifier hands the search over
 * to it instead: the field takes the focus while staying hidden, and the layer
 * covers the screen with its own input. The field is only revealed if the
 * layer never takes over.
 *
 * @class
 * @extends HTMLElement
 */
class AxHeaderSearch extends HTMLElement {
  // Selectors
  #selectors = {
    input: '.header__search-input',
    toggle: '[data-header-search-toggle]',
    header: '.header',
    // Root of the Doofinder layer, rendered at page load when the layer is bound
    searchLayer: '.dfd-root'
  };

  // CSS classes
  #classes = {
    open: 'header--search-open',
    handoff: 'header--search-handoff'
  };

  // Media query matching the breakpoint where the field is always visible
  #desktopMediaQuery = '(min-width: 700px)';

  // Delay after which the field is revealed if the search layer did not take over.
  // Covers the first opening, which waits for the layer to connect to its server.
  #handoffTimeout = 2000;

  // Cached elements
  #elements = {};

  // Guards #handleInputBlur while #focusInput deliberately drops and retakes the focus
  #isReopening = false;

  // Pending fallback of #handOff
  #handoffTimer = null;

  /**
   * Lifecycle - Component connected to DOM
   * @private
   */
  connectedCallback() {
    this.#cacheElements();

    if (!this.#isValidSetup()) return;

    this.#attachEventListeners();
  }

  /**
   * Lifecycle - Component removed from DOM
   * @private
   */
  disconnectedCallback() {
    this.#removeEventListeners();
  }

  /**
   * Cache DOM elements
   * @private
   */
  #cacheElements() {
    this.#elements = {
      input: this.querySelector(this.#selectors.input),
      header: this.closest(this.#selectors.header)
    };
  }

  /**
   * Validate required elements exist
   * @private
   * @returns {boolean}
   */
  #isValidSetup() {
    if (!this.#elements.input) {
      console.error('AxHeaderSearch: search input not found');
      return false;
    }

    if (!this.#elements.header) {
      console.error('AxHeaderSearch: header root element not found');
      return false;
    }

    return true;
  }

  /**
   * Attach event listeners
   * @private
   */
  #attachEventListeners() {
    document.addEventListener('click', this.#handleDocumentClick);
    document.addEventListener('keydown', this.#handleKeydown);
    this.#elements.input.addEventListener('blur', this.#handleInputBlur);
  }

  /**
   * Remove event listeners
   * @private
   */
  #removeEventListeners() {
    document.removeEventListener('click', this.#handleDocumentClick);
    document.removeEventListener('keydown', this.#handleKeydown);
    this.#elements.input?.removeEventListener('blur', this.#handleInputBlur);
    clearTimeout(this.#handoffTimer);
  }

  /**
   * Whether the search field is currently revealed
   * @private
   * @returns {boolean}
   */
  get #isOpen() {
    return this.#elements.header.classList.contains(this.#classes.open);
  }

  /**
   * Whether a search engine layer is bound to the field
   * @private
   * @returns {boolean}
   */
  get #hasSearchLayer() {
    return document.querySelector(this.#selectors.searchLayer) !== null;
  }

  /**
   * Handle clicks on the magnifier button, wherever it lives in the header
   * @private
   * @param {MouseEvent} event - Click event
   */
  #handleDocumentClick = (event) => {
    const toggle = event.target.closest(this.#selectors.toggle);

    if (!toggle) return;

    event.preventDefault();

    // The magnifier always means "search", never "hide". A search engine bound to
    // the field can leave our field revealed after closing its own panel, and a
    // toggle would then spend the tap collapsing the field instead of searching
    // again. Collapsing is handled by #handleInputBlur and by the Escape key.
    // A field already revealed stays so: the layer did not take over last time
    if (this.#hasSearchLayer && !this.#isOpen) {
      this.#handOff(toggle);
      return;
    }

    this.#open(toggle);
  };

  /**
   * Collapse the field when it loses the focus while still empty.
   *
   * A search engine bound to the field takes the focus for its own input as soon
   * as ours receives it. Collapsing here leaves the magnifier button back in its
   * initial state, so coming back to it always takes a single tap. Without this,
   * closing the search engine panel left our field revealed behind it, and the
   * next tap on the magnifier collapsed the field instead of searching again.
   *
   * @private
   */
  #handleInputBlur = () => {
    if (this.#isReopening) return;

    // The search layer took the focus for its own input: the hand-off is done
    this.#endHandoff();

    if (!this.#isOpen || this.#elements.input.value.trim() !== '') return;

    this.#close(document.querySelector(this.#selectors.toggle), false);
  };

  /**
   * Close the search field when Escape is pressed
   * @private
   * @param {KeyboardEvent} event - Keydown event
   */
  #handleKeydown = (event) => {
    if (event.key !== 'Escape' || !this.#isOpen) return;

    const toggle = document.querySelector(this.#selectors.toggle);

    this.#close(toggle);
  };

  /**
   * Reveal the search field and focus it
   * @private
   * @param {HTMLElement} toggle - The magnifier button
   */
  #open(toggle) {
    this.#elements.header.classList.add(this.#classes.open);
    toggle?.setAttribute('aria-expanded', 'true');

    this.#focusInput();
  }

  /**
   * Let the search layer open straight away, without revealing the field.
   *
   * The layer opens when our field gets the focus, then covers the screen and
   * moves the focus into its own input. Revealing our field first only made it
   * flash under the header for a second before the layer took over. The field is
   * now focused while visually hidden (see .header--search-handoff), and only
   * revealed if the layer never takes the focus (layer down, slow network).
   *
   * @private
   * @param {HTMLElement} toggle - The magnifier button
   */
  #handOff(toggle) {
    this.#elements.header.classList.add(this.#classes.handoff);
    this.#focusInput();

    clearTimeout(this.#handoffTimer);
    this.#handoffTimer = setTimeout(() => {
      // Reveal before dropping the hand-off state, so the field never stops
      // being rendered and keeps the focus the visitor may already be typing in
      this.#open(toggle);
      this.#endHandoff();
    }, this.#handoffTimeout);
  }

  /**
   * Leave the hand-off state and cancel its fallback
   * @private
   */
  #endHandoff() {
    clearTimeout(this.#handoffTimer);
    this.#elements.header.classList.remove(this.#classes.handoff);
  }

  /**
   * Give the focus to the field, firing the focus event even if it already had it
   * @private
   */
  #focusInput() {
    // Drop the focus first, so the focus event fires again even when the field
    // already held it. A search engine listening on the field needs that event to
    // reopen its panel. blur() dispatches synchronously, hence the guard.
    this.#isReopening = true;
    this.#elements.input.blur();
    this.#elements.input.focus();
    this.#isReopening = false;
  }

  /**
   * Collapse the search field
   * @private
   * @param {HTMLElement} toggle - The magnifier button
   * @param {boolean} [restoreFocus] - Whether to give the focus back to the toggle
   */
  #close(toggle, restoreFocus = true) {
    this.#elements.header.classList.remove(this.#classes.open);
    toggle?.setAttribute('aria-expanded', 'false');

    if (!restoreFocus) return;

    // On desktop the field never hides, so stealing the focus would be jarring
    if (window.matchMedia(this.#desktopMediaQuery).matches) return;

    toggle?.focus();
  }
}

if (!customElements.get('ax-header-search')) {
  customElements.define('ax-header-search', AxHeaderSearch);
}
